import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Text, useToken } from '@bonusly/design-system';

import type { BizyChartNode } from '../../../schemas/bizy-conversations';
import { HighchartsExportBar } from './highcharts-export-bar';
// Importing highcharts-shared registers the Highcharts export modules (side
// effect) and provides the CSS-var color resolver. Re-export resolveCssColor
// for tests.
import { resolveCssColor } from './highcharts-shared';
import { TruncationCaption } from './truncation-caption';

export { resolveCssColor };

type ChartPayload = BizyChartNode['payload'];
type Series = ChartPayload['series'][number];
type YFormat = ChartPayload['yFormat'];

// Variants plotted on two numeric axes (x/y, plus z for bubble) rather than a
// category axis — they share the scatter rendering path.
const isScatterLike = (variant: ChartPayload['variant']): boolean => variant === 'scatter' || variant === 'bubble';

// Series palette (token names → resolved to values for Highcharts).
const PALETTE_TOKENS = ['primary.bg', 'hashtag.fg', 'green.500', 'orange.400', 'red.400'];

// Diverging bars color each point by the sign of its delta. Literal hex (like
// the heatmap stops) so they rasterize/interpolate without CSS-var resolution.
const DIVERGING_POSITIVE = '#2e9e6b';
const DIVERGING_NEGATIVE = '#e0564f';

// The benchmark/reference line (e.g. peer median). A literal medium gray so the
// thin dashed line is actually visible on the light panel background — the muted
// axis color reads fine as label text but disappears as a 1px stroke.
const REFERENCE_LINE_COLOR = '#64748b';

interface BizyChartProps {
	variant: ChartPayload['variant'];
	title?: string;
	xLabel?: string;
	yLabel?: string;
	/** Bubble only: the name of the size (z) metric, e.g. "Team headcount". */
	sizeLabel?: string;
	yFormat: YFormat;
	series: Series[];
	referenceValue?: number;
	referenceLabel?: string;
	/** Drill: clicking a bar/point sends a scoped follow-up for its category
	 *  (and, on a multi-series chart, the clicked series). */
	onDrill?: (label: string, seriesName?: string) => void;
	/** Full group count when a bar/breakdown was trimmed to a subset; drives the
	 *  "N of M" caption. Absent/equal to shown groups → no caption. */
	totalGroups?: number;
	/** Ranking direction for the "N of M" caption (top/bottom); absent = neutral. */
	selection?: 'top' | 'bottom';
	/** Expand: re-request the full set when the groups were trimmed. */
	onExpand?: () => void;
}

export function formatValue(value: number | string | null | undefined, format: YFormat): string {
	if (typeof value !== 'number' || Number.isNaN(value)) return value == null ? '' : String(value);
	switch (format) {
		case 'percent':
			return `${value}%`;
		case 'currency':
			return `$${value.toLocaleString()}`;
		default:
			return value.toLocaleString();
	}
}

// Series names double as Highcharts series names / React keys, so a backend
// collision would be confusing. Suffix repeats ("Recognitions", "… (2)").
function withUniqueNames(series: Series[]): Series[] {
	const seen = new Map<string, number>();
	return series
		.filter((s) => s.name?.trim().length > 0)
		.map((s) => {
			const count = (seen.get(s.name) ?? 0) + 1;
			seen.set(s.name, count);
			return count === 1 ? s : { ...s, name: `${s.name} (${count})` };
		});
}

function distinctLabels(series: Series[]): string[] {
	const seen = new Set<string>();
	const labels: string[] = [];
	series.forEach((s) =>
		s.points.forEach((p) => {
			if (p.label && !seen.has(p.label)) {
				seen.add(p.label);
				labels.push(p.label);
			}
		})
	);
	return labels;
}

function pointAt(series: Series, label: string) {
	return series.points.find((p) => p.label === label);
}

interface BuildOptionsArgs {
	variant: ChartPayload['variant'];
	yFormat: YFormat;
	series: Series[];
	palette: string[];
	axisColor: string;
	gridColor: string;
	/** Panel surface color — used as a thin halo around scatter/bubble markers so
	 *  overlapping points stay visually distinct. */
	surfaceColor: string;
	/** Axis titles — used as the x/y metric names on scatter/bubble plots. */
	xLabel?: string;
	yLabel?: string;
	/** Bubble only: the name of the size (z) metric, shown in the tooltip. */
	sizeLabel?: string;
	/** i18n fallbacks for the scatter/bubble tooltip + CSV when the agent omits a
	 *  metric name. English defaults keep this builder unit-testable on its own. */
	fallbackLabels?: { entity: string; size: string };
	referenceValue?: number;
	referenceLabel?: string;
	/** When set, points are clickable and call this with the category label and,
	 *  for a multi-series chart, the clicked series name. */
	onPointClick?: (label: string, seriesName?: string) => void;
}

// Pure options builder (no DOM) so the chart config is unit-testable. Variants:
//   bar       — horizontal bars, value shown as a data label, value axis hidden.
//   line      — series over the category axis with a labelled value axis.
//   stacked   — bars stacked per category (composition); value axis shown.
//   diverging — bars from a zero baseline, colored by sign (deltas); value axis shown.
// All put category labels on the x-axis, mark `partial` points with a trailing
// "*", and draw an optional reference line on the value axis.
export function buildChartOptions({
	variant,
	yFormat,
	series,
	palette,
	axisColor,
	gridColor,
	surfaceColor,
	xLabel,
	yLabel,
	sizeLabel,
	fallbackLabels = { entity: 'Entity', size: 'Size' },
	referenceValue,
	referenceLabel,
	onPointClick,
}: BuildOptionsArgs): Highcharts.Options {
	const visible = withUniqueNames(series);
	const labels = distinctLabels(visible);
	const stacked = variant === 'stacked';
	const diverging = variant === 'diverging';
	const scatterLike = isScatterLike(variant);
	const bubble = variant === 'bubble';
	const isBar = variant === 'bar' || stacked || diverging;
	// Plain bars show the value as a data label so the value axis can hide;
	// stacked + diverging keep a labelled value axis (stack totals / zero
	// baseline) and drop the per-bar labels (they'd clutter a stack or a delta).
	const showValueAxis = !isBar || stacked || diverging;
	const colorAt = (i: number) => palette[i % palette.length];

	const dataSeries = (
		scatterLike
			? // scatter/bubble plot numeric x/y (and z for bubble) per entity; the
				// point name is the entity label (tooltip + drill). A thin surface-colored
				// marker border haloes each point so overlapping ones stay distinct.
				visible.map((s, i) => ({
					type: bubble ? 'bubble' : 'scatter',
					name: s.name,
					color: colorAt(i),
					marker: { lineWidth: 1, lineColor: surfaceColor },
					data: s.points.map((p) => ({
						x: p.x ?? null,
						y: p.y ?? null,
						...(bubble ? { z: p.z ?? null } : {}),
						name: p.label,
						custom: { partial: !!p.partial },
					})),
				}))
			: visible.map((s, i) => ({
					type: isBar ? 'bar' : 'line',
					name: s.name,
					// Diverging colors each point by the sign of its delta; the others use the palette.
					...(diverging ? {} : { color: colorAt(i) }),
					data: labels.map((label) => {
						const point = pointAt(s, label);
						const y = point?.value ?? null;
						const cell = { y, custom: { partial: !!point?.partial } };
						return diverging && typeof y === 'number' ? { ...cell, color: y < 0 ? DIVERGING_NEGATIVE : DIVERGING_POSITIVE } : cell;
					}),
					...(isBar ? {} : { marker: { enabled: true, radius: 3 } }),
				}))
	) as Highcharts.SeriesOptionsType[];

	const plotLines: Highcharts.YAxisPlotLinesOptions[] =
		referenceValue == null
			? []
			: [
					{
						value: referenceValue,
						color: REFERENCE_LINE_COLOR,
						dashStyle: 'Dash',
						width: 1.5,
						zIndex: 4,
						label: referenceLabel
							? { useHTML: true, text: `${referenceLabel}: ${formatValue(referenceValue, yFormat)}`, style: { color: axisColor, fontSize: '11px' } }
							: undefined,
					},
				];

	const valueLabel = function (this: { y?: number | null; point?: { options?: { custom?: { partial?: boolean } } } }): string {
		const formatted = formatValue(this.y ?? null, yFormat);
		return this.point?.options?.custom?.partial ? `${formatted}*` : formatted;
	};

	return {
		chart: { type: scatterLike ? variant : isBar ? 'bar' : 'line', backgroundColor: 'transparent', spacing: [8, 8, 8, 8] },
		title: { text: '' },
		credits: { enabled: false },
		// scatter/bubble share one series for x & y, so Highcharts' default CSV
		// headers are "Category", "<series> (x)", "<series> (y)". Name the columns
		// by metric instead (mirrors the markdown fallback: Entity / x / y / size).
		exporting: scatterLike
			? {
					enabled: false,
					csv: {
						columnHeaderFormatter(item: { coll?: string } | null, key?: string) {
							if (!item || item.coll === 'xAxis') return fallbackLabels.entity;
							if (key === 'x') return xLabel ?? 'X';
							if (key === 'y') return yLabel ?? 'Y';
							if (key === 'z') return sizeLabel ?? fallbackLabels.size;
							return false;
						},
					},
				}
			: { enabled: false },
		// useHTML for the same reason as the axis labels — SVG legend text doesn't
		// render until a reflow (e.g. hover) in this embedded context.
		legend: { enabled: visible.length > 1, useHTML: true, itemStyle: { color: axisColor, fontWeight: 'normal' } },
		xAxis: scatterLike
			? {
					// scatter/bubble use a numeric x-axis (no categories); the title is the x-metric name.
					// Axis names render as an HTML caption below the chart (the narrow panel clips SVG axis titles).
					title: { text: '' },
					lineColor: gridColor,
					tickColor: gridColor,
					gridLineColor: gridColor,
					labels: {
						useHTML: true,
						style: { color: 'var(--chakra-colors-text-muted)', fontSize: '11px' },
						formatter: function () {
							return formatValue(this.value as number, 'number');
						},
					},
				}
			: {
					categories: labels,
					lineColor: gridColor,
					tickColor: gridColor,
					// HTML labels: in the side panel Highcharts can't reliably measure SVG
					// label widths (it reserves no left margin and clips them), whereas
					// HTML labels measure reliably and auto-reserve space. Pairing this
					// with an HTML tooltip (below) keeps the tooltip stacked above them.
					// `step: 1` forces every category label on bar/stacked/diverging — the
					// category axis is one row per group, and Highcharts otherwise thins
					// labels at narrow widths (dropping e.g. the middle month). Lines keep
					// auto-thinning so many time buckets don't crowd.
					labels: { useHTML: true, step: isBar ? 1 : undefined, style: { color: 'var(--chakra-colors-text-muted)', fontSize: '11px' } },
				},
		yAxis: {
			title: { text: '' },
			min: diverging || scatterLike ? undefined : 0,
			gridLineColor: gridColor,
			// Diverging adds a zero baseline so positive/negative deltas read clearly.
			plotLines: diverging ? [{ value: 0, color: axisColor, width: 1, zIndex: 3 }, ...plotLines] : plotLines,
			visible: showValueAxis,
			labels: showValueAxis
				? {
						// useHTML for the same reason as the x-axis: the side panel is too
						// narrow for Highcharts to measure SVG label widths, so it reserves
						// no left margin and clips the value labels (they show at wider widths).
						useHTML: true,
						style: { color: 'var(--chakra-colors-text-muted)', fontSize: '11px' },
						formatter: function () {
							return formatValue(this.value as number, yFormat);
						},
					}
				: { enabled: false },
		},
		tooltip: {
			// Inside tooltips are SVG groups that render behind the HTML axis-label
			// overlay, so render OUTSIDE (a body-level div) to escape that stacking
			// — with a high z-index, since the default outside z-index sits behind
			// the side panel.
			useHTML: true,
			outside: true,
			style: { zIndex: 9999 },
			formatter: function (this: Highcharts.TooltipFormatterContextObject) {
				if (scatterLike) {
					const entity = (this.point as { name?: string }).name ?? '';
					const seriesPrefix = visible.length > 1 && this.series?.name ? `${this.series.name}<br/>` : '';
					const parts = [`${xLabel ?? 'x'}: ${formatValue(this.x as number, 'number')}`, `${yLabel ?? 'y'}: ${formatValue(this.y ?? null, yFormat)}`];
					if (bubble) parts.push(`${sizeLabel ?? fallbackLabels.size}: ${formatValue((this.point as { z?: number }).z ?? null, 'number')}`);
					return `<b>${entity}</b><br/>${seriesPrefix}${parts.join('<br/>')}`;
				}
				const label = this.key ?? '';
				const name = this.series?.name ? `${this.series.name}: ` : '';
				return `<b>${label}</b><br/>${name}${formatValue(this.y ?? null, yFormat)}`;
			},
		},
		plotOptions: {
			bar: {
				stacking: stacked ? 'normal' : undefined,
				// Open up the gap between category rows (Highcharts otherwise fills
				// most of each band, so rows read as cramped — worst on stacked).
				pointPadding: 0.2,
				groupPadding: 0.25,
				borderRadius: { radius: 3, where: 'all', scope: 'point' },
				dataLabels: {
					enabled: !stacked,
					inside: false,
					align: 'left',
					overflow: 'allow',
					crop: false,
					formatter: valueLabel,
					style: { fontWeight: 'normal', color: 'var(--chakra-colors-text-default)', textOutline: 'none' },
				},
			},
			line: { dataLabels: { enabled: false } },
			// Drill: clicking a point sends a scoped follow-up for that category.
			...(onPointClick
				? {
						series: {
							cursor: 'pointer',
							point: {
								events: {
									click: function (this: Highcharts.Point & { category?: string | number; series?: { name?: string } }) {
										// Prefer the point name (entity); `category` on a numeric scatter axis is the x-VALUE.
										onPointClick(String(this.name ?? this.category ?? ''), this.series?.name);
									},
								},
							},
						},
					}
				: {}),
		},
		series: dataSeries,
	};
}

/**
 * Renders the Data Agent's Chart node — the evidence visual that follows the
 * verdict — with Highcharts (kaleidoscope's standard charting library, used
 * across the admin dashboards). `bar` for rankings/breakdowns, `line` for a
 * metric over time. Category labels and values render reliably (unlike
 * recharts' SVG axis text); `partial` buckets are marked with a trailing "*".
 *
 * Display-only — no drill affordance. A hover-revealed action bar exports the
 * chart as CSV (the series data) or PNG (rasterized client-side via Highcharts'
 * offline-exporting module).
 */
export function BizyChart({
	variant,
	title,
	xLabel,
	yLabel,
	sizeLabel,
	yFormat,
	series,
	referenceValue,
	referenceLabel,
	onDrill,
	totalGroups,
	selection,
	onExpand,
}: BizyChartProps) {
	const chartRef = useRef<HighchartsReact.RefObject>(null);
	const { t } = useTranslation('admin_bizy');
	const palette = useToken('colors', PALETTE_TOKENS).map(resolveCssColor);
	const [axisColor, gridColor, surfaceColor] = useToken('colors', ['text.muted', 'border.soft', 'bg.surface']).map(resolveCssColor);

	const visibleSeries = withUniqueNames(series);
	const labels = distinctLabels(visibleSeries);
	const scatterLike = isScatterLike(variant);
	// Scatter/bubble plot from each point's x/y (labels optional); bar/line need
	// distinct categories. Don't hide a label-less scatter that still has points.
	const hasPlottableData = scatterLike ? visibleSeries.some((s) => s.points.length > 0) : labels.length > 0;
	if (visibleSeries.length === 0 || !hasPlottableData) return null;

	const options = buildChartOptions({
		variant,
		yFormat,
		series,
		palette,
		axisColor,
		gridColor,
		surfaceColor,
		xLabel,
		yLabel,
		sizeLabel,
		fallbackLabels: { entity: t('chartFallback.entity'), size: t('chartFallback.size') },
		referenceValue,
		referenceLabel,
		onPointClick: onDrill,
	});
	// The "N" the truncation caption discloses against M. Scatter/bubble plot every
	// point (one per entity), so count points; bar/line render one bar per distinct
	// category, so mirror the bar-building path (distinctLabels over uniquely-named
	// series) — N matches the bars drawn, not raw points (blank/unnamed never render).
	const shownGroups = scatterLike ? withUniqueNames(series).reduce((n, s) => n + s.points.length, 0) : distinctLabels(withUniqueNames(series)).length;

	// Bars need vertical room per category (grouped bars stack per series; stacked
	// and diverging are one bar per category); lines use a fixed height.
	const barsPerCategory = variant === 'bar' ? visibleSeries.length : 1;
	// Each category row needs room for its bar(s) AND its axis label; too little
	// and Highcharts thins/overlaps the labels (e.g. a dropped middle month).
	// The fixed overhead also leaves space for the legend below the plot.
	// Scatter/bubble are a fixed-size plot (like line), not per-row.
	const height = variant === 'line' ? 260 : scatterLike ? 320 : labels.length * (barsPerCategory * 20 + 28) + 64;

	// Axis caption (HTML, below the chart — the panel clips SVG axis titles).
	// Scatter/bubble name both numeric axes; bar/line just name the value axis.
	const axisCaption = scatterLike
		? [yLabel && `y: ${yLabel}`, xLabel && `x: ${xLabel}`, variant === 'bubble' && sizeLabel && `size: ${sizeLabel}`].filter(Boolean).join('  ·  ')
		: yLabel;

	return (
		<Flex direction="column" gap={2} mb={4} data-testid="bizy-chart" sx={{ '&:hover .bizy-chart-actions': { opacity: 1 } }}>
			{title ? (
				<Text fontSize="md" fontWeight="semibold" color="text.default">
					{title}
				</Text>
			) : null}
			<Box width="100%" sx={{ '.highcharts-container': { margin: '0 auto' } }}>
				<HighchartsReact ref={chartRef} highcharts={Highcharts} options={{ ...options, chart: { ...options.chart, height } }} />
			</Box>
			{axisCaption ? (
				<Text fontSize="xs" color="text.muted" textAlign="center" textTransform="uppercase" letterSpacing="0.08em" fontWeight="semibold" mt={1}>
					{axisCaption}
				</Text>
			) : null}
			{totalGroups != null ? <TruncationCaption shown={shownGroups} total={totalGroups} selection={selection} onExpand={onExpand} /> : null}
			<HighchartsExportBar
				chartRef={chartRef}
				title={title}
				xLabel={xLabel}
				yLabel={yLabel}
				subtitle={variant === 'bubble' && sizeLabel ? `Bubble size: ${sizeLabel}` : undefined}
				fallback="chart"
				variant={variant}
				idPrefix="bizy-chart"
			/>
		</Flex>
	);
}
